import { AppContext } from 'Api/AppContext';
import { ISmartObjectService } from 'Api/Contracts/Interfaces';
import { PointCloud } from 'Api/Dto/PointCloud';
import { AssetPanoramaGroup, BindingMode, ExploreService, LocalizerService, Navigation, Project } from 'Api/Services';
import { PointCloudBehavior } from 'App/Areas/Explore/Components/Behaviors/PointCloudBehavior';
import VoPointCloudMeasure from 'App/Areas/Explore/Components/VoPointCloudMeasure.vue';
import { IPlayerContext } from 'App/Areas/Shares/IPlayerContext';
import { PlayerConverter } from 'App/Areas/Shares/PlayerActionConverters';
import { PlayerContext } from 'App/Areas/Shares/PlayerContext';
import { getComponentFromType } from 'App/Areas/Shares/RendererComponents';
import VoSearchBox from 'App/Areas/Shares/VoSearchBox.vue';
import { injectTypes } from 'App/injectTypes';
import { lazyInject, serviceProvider } from 'App/Inversify.config';
import { IVisonUriService } from 'App/Services/UriServices/Core';
import { IConfiguration, IIconProvider, isPanoramaConfiguration, ISphericalCoordinates } from 'Framework/Components/Controls/XView/Core';
import { IViewer } from 'Framework/Components/Controls/XView/IViewer';
import { Viewer360 } from 'Framework/Components/Controls/XView/XView.Viewer360';
import { Component, Prop, UIElement, Vue, Watch } from 'Framework/Components/UIElement';
import { UrlHelper } from 'Framework/Helpers/UrlHelper';
import { IDialogService } from 'Framework/Services/IDialogService';
import { CreateElement, VNode } from 'vue';

@Component({
    name: 'vo-player',
    components: { VoSearchBox, VoPointCloudMeasure }
})
export default class VoPlayer extends UIElement<IPlayerContext> {
    public mounted(): void {
        //if(pointCloudModuleAllowed) { not used yet
        this._pointCloudBehavior = new PointCloudBehavior(
            async (e: ISphericalCoordinates) => await this._onPointerPickedAsync(e),
            this._localizerService
        );
        //}

        UIElement.Bus.$on('configuration-changed', this._onConfigurationChanged);
        UIElement.Bus.$on('player-error-raised', this._onPlayerErrorRaised);
    }

    public beforeDestroy(): void {
        UIElement.Bus.$off('configuration-changed', this._onConfigurationChanged);
        UIElement.Bus.$off('player-error-raised', this._onPlayerErrorRaised);
    }

    public render(createElement: CreateElement, context: any): VNode {
        if (this.error) {
            let uiElement = this.project.layout;

            return createElement('x-grid',
                {
                    staticStyle: {
                        'position': 'relative'
                    }
                },
                [
                    this._uiElementToVNode(uiElement, createElement, context, {
                        'z-index': 0
                    }),
                    this.$scopedSlots['error']?.({ error: this.error, clear: () => this.error = null })?.[0]
                ]
            );
        }
        else if (this.project) {
            let uiElement = this.project.layout;

            return this._uiElementToVNode(uiElement, createElement, context);
        }
        else {
            return this.$scopedSlots['loading']?.({/*No props*/ })?.[0]
                ?? createElement('div');
        }
    }

    public static createDataContext(player: VoPlayer, value: Project = null): IPlayerContext {
        let dataContext = new PlayerContext(
            player,
            serviceProvider.get<AppContext>(AppContext),
            serviceProvider.get<IVisonUriService>(injectTypes.IVisonUriService),
            serviceProvider.get<IDialogService>(injectTypes.IDialogService),
            serviceProvider.get<IIconProvider>(injectTypes.IIconProvider),
            serviceProvider.get<ISmartObjectService>(injectTypes.ISmartObjectService),
            value ?? null
        );

        if (value) {
            for (let index in value.datasources) {
                let datasource = value.datasources[index];
                dataContext[datasource.name] = datasource.value;
            }

            dataContext.validatedToken = value.validatedToken;
        }

        return Vue.observable(dataContext);
    }

    @Watch('project')
    public onProjectPropertyChanged(value: Project, oldValue: Project): void {
        if (value) {
            let dataContext = VoPlayer.createDataContext(this, value);

            let events = JSON.parse(this.project.events ?? null) ?? {};
            let onLoadedEventString = events.onLoaded;
            let onLoadedEvent: Function = this._createLoadedEventFromViewState(dataContext);

            if (!onLoadedEvent && typeof(onLoadedEventString) === 'string') {
                onLoadedEvent = dataContext.parseFunction(onLoadedEventString);
            }

            onLoadedEvent = onLoadedEvent || this.loadFirstPanorama;

            this._dataContext = dataContext;
            this.loadFirstNavigation();
            onLoadedEvent();
        }
        else {
            this._dataContext = VoPlayer.createDataContext(this);
        }
    }

    private _createLoadedEventFromViewState(dataContext: IPlayerContext): Function {
        let onLoadedEvent: Function = null;
        let startOrientation: any = null;
        let viewState = UrlHelper.getUrlHashParameters();

        if (viewState) {
            if ('startOrientation' in viewState) {
                startOrientation = JSON.parse(viewState['startOrientation']);
            }

            if ('startPanoramaGroupName' in viewState) {
                let startPanoramaGroupName = viewState['startPanoramaGroupName'];

                onLoadedEvent = () => dataContext.goToPanorama(startPanoramaGroupName, startOrientation);
            }

            if ('startPanorama' in viewState) {
                let startPanoramaId = Number(viewState['startPanorama']);

                onLoadedEvent = () => dataContext.goToPanoramaVersion(startPanoramaId, startOrientation);
            }

            delete viewState['startPanorama'];
            delete viewState['startOrientation'];
            delete viewState['startPanoramaGroupName'];
        }

        return onLoadedEvent;
    }

    private _onPlayerErrorRaised(event: { sender: object, args: any }): void {
        this.error = event.args;
    }

    private loadFirstNavigation(): void {
        let datasources = this.project.datasources.filter(ds => ds.type == 'Array' && ds.of == 'Navigation');

        if (Array.isArray(datasources) && datasources.length == 1) {
            let navigationSource = datasources[0].value as Array<Navigation>;
            if (Array.isArray(navigationSource) && navigationSource.length > 0) {
                this._dataContext.loadNavigation(navigationSource[0]);
            }
        }
        else {
            console.log(`loadFirstNavigation : datasource is ${datasources} and lenght : ${datasources ? datasources.length : null}`);
        }
    }

    private loadFirstPanorama(): void {
        let datasources = this.project.datasources.filter(ds => ds.type == 'Array' && ds.of == 'AssetPanoramaGroup');

        if (Array.isArray(datasources) && datasources.length == 1) {
            let panoramaSource = datasources[0].value as Array<AssetPanoramaGroup>;
            if (Array.isArray(panoramaSource) && panoramaSource.length > 0) {
                this._dataContext.goToPanorama(panoramaSource[0]);
            }
        }
        else {
            console.log(`loadFirstPanorama : datasource is ${datasources} and lenght : ${datasources ? datasources.length : null}`);
        }
    }

    private async _onConfigurationChanged(obj: { sender: IViewer, args: IConfiguration }): Promise<void> {
        this._currentPlayer = obj.sender;

        if (isPanoramaConfiguration(obj.args)) {
            this._dataContext.currentOrientation = obj.args.startOrientation;
        }

        this._dataContext.isPanoramaHasPointCloud = await this._exploreService
            .scanExistAsync(
                this.project.id,
                this._dataContext.currentPanorama.assetItemId);

        if (!this._pointCloudBehavior) {
            return;
        }

        this._pointCloudBehavior.detach();

        if (this._currentPlayer instanceof Viewer360
            && this._dataContext.isPanoramaHasPointCloud
        ) {
            this._pointCloudBehavior.attach(this._currentPlayer);
        }
    }

    private async _onPointerPickedAsync(sphericalCoordinates: ISphericalCoordinates): Promise<PointCloud> {
        return await this._exploreService.readScanAtAsync(
            this.project.id,
            this._dataContext?.currentPanorama?.assetItemId,
            sphericalCoordinates.theta,
            sphericalCoordinates.phi
        );
    }

    private _uiElementToVNode(uiElement: any, createElement: CreateElement, context: any, staticStyle: Record<string, any> = null): VNode {
        if (uiElement) {
            let typeOfComponent = getComponentFromType(uiElement.type);

            if (typeOfComponent) {
                let props = {};
                let on = {};

                let configurationElement = {};
                props['configurationElement'] = configurationElement;
                if ('playerContext' in typeOfComponent.prototype) {
                    props['playerContext'] = this._dataContext;
                }

                for (const propKey in uiElement) {
                    const propValue: any = uiElement[propKey];

                    if (propKey in typeOfComponent.prototype) {
                        if (propValue && typeof (propValue) === 'object' && 'path' in propValue) {
                            const bindingPath = propValue.path;
                            const bindingMode = propValue.mode;
                            const bindingConverterName = propValue.converter;

                            const value = this._dataContext[bindingPath];

                            const bindingConverter = PlayerConverter.getConverter(bindingConverterName);

                            props[propKey] = bindingConverter
                                ? bindingConverter.converter(this._dataContext, value)
                                : value;

                            if (bindingMode == BindingMode.TwoWay) {
                                on[`update:${propKey}`] = ($event: any) => { this._dataContext[bindingPath] = $event };
                            }
                        }
                        else {
                            let propsOfPrototype = (typeOfComponent as any).options?.props
                                ?? typeOfComponent.prototype?.$props
                                ?? {};

                            let record = (propsOfPrototype as Record<string, any>)[propKey];
                            if (record && 'type' in record) {
                                switch (record.type) {
                                    case Object:
                                    case Array:
                                        props[propKey] = JSON.parse(propValue);
                                        break;
                                    case Number: props[propKey] = Number(propValue);
                                        break;
                                    case Function: props[propKey] = this._dataContext.parseFunction(propValue);
                                        break;
                                    default: props[propKey] = propValue;
                                        break;
                                }
                            }
                            else {
                                props[propKey] = propValue;
                            }

                            on[`update:${propKey}`] = ($event: any) => { uiElement[propKey] = $event };
                        }

                    }
                    else {
                        configurationElement[propKey] = propValue;
                    }
                }

                let children: Array<VNode> = [];
                if ('children' in uiElement && Array.isArray(uiElement.children)) {
                    children = uiElement.children.map((c: any) => this._uiElementToVNode(c, createElement, null));
                }

                let result = createElement(
                    typeOfComponent,
                    {
                        props: props,
                        on: on,
                        staticStyle: staticStyle
                    },
                    children
                );

                return result;
            }
        }

        return null;
    }

    public error: any = null;

    @Prop({ type: Object, required: false, default: null })
    public project: Project;

    @lazyInject(ExploreService)
    private readonly _exploreService: ExploreService;

    @lazyInject(LocalizerService)
    private readonly _localizerService: LocalizerService;

    private _currentPlayer: IViewer;
    private _pointCloudBehavior: PointCloudBehavior;
}
